import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard } from '../api/vendorApi';
import { VpSpinner, VpStat, VpStatusBadge, VpEmpty, fmtDate } from '../components/VpUI';
import VpIcon from '../components/VpIcon';

const VendorDashboard = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState<any>(null);
	const [error, setError] = useState('');

	useEffect(() => {
		(async () => {
			try {
				const d = await getDashboard();
				setData(d);
			} catch (err: any) {
				setError(err?.apiMessage || 'Could not load your dashboard.');
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	if (loading) return <VpSpinner />;
	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	const c = data?.counts || {};
	const recent = data?.recentQuotations || [];
	const needsAttention: { text: string; tint: string; to: string }[] = [];
	if (c.pendingToQuote > 0)
		needsAttention.push({
			text: `${c.pendingToQuote} requisition(s) awaiting your quotation`,
			tint: 'var(--vp-warning)',
			to: '/vendor-portal/requisitions',
		});
	if (c.pendingUploads > 0)
		needsAttention.push({
			text: `${c.pendingUploads} accepted order(s) awaiting challan/invoice upload`,
			tint: 'var(--vp-info)',
			to: '/vendor-portal/challans',
		});

	return (
		<div>
			{data?.hasAcceptedCelebration && (
				<div className='vp-congrats'>
					<div className='vp-congrats-emoji'>
						<VpIcon name='celebrate' size={40} />
					</div>
					<div>
						<h3>Congratulations!</h3>
						<p>
							You have {c.acceptedQuotations} accepted quotation(s). Log your challans
							and invoices from the Challans &amp; Invoices section.
						</p>
					</div>
				</div>
			)}

			<div className='vp-stats'>
				<VpStat
					icon='requisitions'
					tint='primary'
					value={c.assignedRequisitions ?? 0}
					label='Assigned Requisitions'
				/>
				<VpStat
					icon='pending'
					tint='warning'
					value={c.pendingToQuote ?? 0}
					label='Pending to Quote'
				/>
				<VpStat
					icon='quotations'
					tint='info'
					value={c.totalQuotations ?? 0}
					label='Quotations Submitted'
				/>
				<VpStat
					icon='accepted'
					tint='success'
					value={c.acceptedQuotations ?? 0}
					label='Accepted (Won)'
				/>
				<VpStat
					icon='rejected'
					tint='danger'
					value={c.rejectedQuotations ?? 0}
					label='Rejected'
				/>
				<VpStat
					icon='uploads'
					tint='info'
					value={c.pendingUploads ?? 0}
					label='Pending Uploads'
				/>
			</div>

			<div className='vp-grid-2'>
				<div className='vp-card'>
					<div className='vp-card-head'>
						<h3>Recent Quotations</h3>
						<button
							type='button'
							className='vp-btn vp-btn-ghost vp-input-sm'
							onClick={() => navigate('/vendor-portal/quotations')}>
							View all
						</button>
					</div>
					{recent.length === 0 ? (
						<VpEmpty icon='quotations' text='No quotations submitted yet.' />
					) : (
						<div className='vp-table-wrap'>
							<table className='vp-table'>
								<thead>
									<tr>
										<th>Quotation</th>
										<th>Requisition</th>
										<th>Date</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{recent.map((q: any) => (
										<tr
											key={q._id}
											style={{ cursor: 'pointer' }}
											onClick={() =>
												navigate(`/vendor-portal/quotations/${q._id}`)
											}>
											<td style={{ fontWeight: 600 }}>{q.quotation_no}</td>
											<td>{q.requisition?.requisition_no || '—'}</td>
											<td>{fmtDate(q.quotation_date || q.createdAt)}</td>
											<td>
												<VpStatusBadge status={q.quotation_status} />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				<div className='vp-card vp-card-pad'>
					<h3 style={{ marginTop: 0, fontSize: 16 }}>Needs Your Attention</h3>
					{needsAttention.length === 0 ? (
						<div
							className='vp-muted'
							style={{ padding: '18px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
							<VpIcon name='check' size={18} /> You&apos;re all caught up. Nothing needs
							action right now.
						</div>
					) : (
						needsAttention.map((n, i) => (
							<div
								key={i}
								className='vp-attn-item'
								style={{ cursor: 'pointer' }}
								onClick={() => navigate(n.to)}>
								<span className='vp-attn-dot' style={{ background: n.tint }} />
								<span>{n.text}</span>
							</div>
						))
					)}
				</div>
			</div>
		</div>
	);
};

export default VendorDashboard;
